import React, { useState, useEffect } from 'react';
import './AdminEvents.css';
import { supabase } from '../../config/supabase';
import { useRealtimeEvents } from '../../hooks/useRealtime';
import Modal from './Modal';

const AdminEvents = () => {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        date: '',
        venue: '',
        category: '',
        last_date_reg: '',
        reg_link: ''
    });
    const [editingId, setEditingId] = useState(null);

    // Modal State
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        type: 'confirm',
        title: '',
        message: '',
        onConfirm: null
    });

    // Real-time: Refresh when events change
    useRealtimeEvents(() => {
        console.log('Events updated in real-time');
        fetchEvents();
    });

    useEffect(() => {
        fetchEvents();
    }, []);

    // Fetch directly from Supabase
    const fetchEvents = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('events')
                .select('*')
                .order('created_at', { ascending: false });
            
            if (error) throw error;
            
            setEvents(data || []);
            setLastUpdated(new Date().toLocaleTimeString());
        } catch (error) {
            console.error('Failed to fetch events:', error);
            setEvents([]);
        }
        setIsLoading(false);
    };

    const closeModal = () => {
        setModalConfig({ ...modalConfig, isOpen: false });
    };

    const showModal = (type, title, message, onConfirm = null) => {
        setModalConfig({ isOpen: true, type, title, message, onConfirm });
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (editingId) {
            try {
                const { error } = await supabase
                    .from('events')
                    .update(formData)
                    .eq('id', editingId);
                
                if (error) throw error;
                
                showModal('success', 'Success!', 'Event updated successfully.');
                setEditingId(null);
                fetchEvents();
            } catch (error) {
                showModal('error', 'Update Failed', error.message);
            }
        } else {
            try {
                const { error } = await supabase
                    .from('events')
                    .insert([formData]);
                
                if (error) throw error;
                
                showModal('success', 'Success!', 'New event added successfully.');
                fetchEvents();
            } catch (error) {
                showModal('error', 'Add Failed', error.message);
            }
        }

        setFormData({ name: '', date: '', venue: '', category: '', last_date_reg: '', reg_link: '' });
    };

    const handleEdit = (event) => {
        setFormData({
            name: event.name || '',
            date: event.date || '',
            venue: event.venue || '',
            category: event.category || '',
            last_date_reg: event.last_date_reg || '',
            reg_link: event.reg_link || ''
        });
        setEditingId(event.id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const confirmDelete = (id) => {
        showModal(
            'confirm',
            'Delete Event?',
            'Are you sure you want to delete this event? This action cannot be undone.',
            async () => {
                try {
                    const { error } = await supabase
                        .from('events')
                        .delete()
                        .eq('id', id);
                    
                    if (error) throw error;
                    
                    closeModal();
                    fetchEvents();
                } catch (error) {
                    closeModal();
                    setTimeout(() => showModal('error', 'Error', error.message), 300);
                }
            }
        );
    };

    return (
        <div className="admin-events-container">
            <Modal
                isOpen={modalConfig.isOpen}
                type={modalConfig.type}
                title={modalConfig.title}
                message={modalConfig.message}
                onConfirm={modalConfig.onConfirm}
                onCancel={closeModal}
            />

            <h2>Manage Events (Real-Time)</h2>

            <div className="event-form-card">
                <h3>{editingId ? 'Edit Event' : 'Add New Event'}</h3>
                <form onSubmit={handleSubmit} className="event-form">
                    <input type="text" name="name" placeholder="Event Name" value={formData.name} onChange={handleChange} required />
                    <input type="text" name="date" placeholder="Date (e.g., Feb 24-26, 2025)" value={formData.date} onChange={handleChange} required />
                    <input type="text" name="venue" placeholder="Venue" value={formData.venue} onChange={handleChange} required />
                    <input type="text" name="category" placeholder="Category" value={formData.category} onChange={handleChange} required />
                    <input type="text" name="last_date_reg" placeholder="Last Date of Reg." value={formData.last_date_reg} onChange={handleChange} required />
                    <input type="text" name="reg_link" placeholder="Registration Link" value={formData.reg_link} onChange={handleChange} required />
                    <button type="submit" className="save-btn">{editingId ? 'Update Event' : 'Add Event'}</button>
                    {editingId && (
                        <button type="button" className="cancel-btn" onClick={() => {
                            setEditingId(null);
                            setFormData({ name: '', date: '', venue: '', category: '', last_date_reg: '', reg_link: '' });
                        }}>Cancel</button>
                    )}
                </form>
            </div>

            <div className="events-list">
                <h3>Current Events</h3>
                {isLoading ? <p>Loading events...</p> : (
                    <table>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Date</th>
                                <th>Venue</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((event) => (
                                <tr key={event.id}>
                                    <td>{event.name}</td>
                                    <td>{event.date}</td>
                                    <td>{event.venue}</td>
                                    <td>
                                        <button onClick={() => handleEdit(event)} className="edit-btn">Edit</button>
                                        <button onClick={() => confirmDelete(event.id)} className="delete-btn">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default AdminEvents;
